<template>
  <div class="guarantor-agreement">
    <p class="body-regular gray-900">
      Analise os dados do pedido antes de concordar em ser avalista desta compra.
    </p>

    <p class="small-regular gray-500 mt-12">
      Esta proposta tem validade de 48 horas.
    </p>

    <div class="d-flex with-tooltip">
      <h4 class="title mt-36">DADOS DO AVALIZADO</h4>
      <QuestionMarkCircleIcon size="24" color="#64748B" @click="handleTooltipClick" />
    </div>

    <div v-if="tooltipActive" class="tooltip">
      <p class="small-regular">
        É a pessoa que pediu sua ajuda para realizar o pagamento.
      </p>
    </div>

    <div class="user-info--wrapper">
      <div class="user-info">
        <p>Nome</p>
        <p>{{ guaranteedTransaction.name | capitalizeInSpaces }}</p>
      </div>

      <div class="user-info">
        <p>CPF</p>
        <p>{{ guaranteedTransaction.cpf | cpfFilter }}</p>
      </div>

      <div class="user-info">
        <p>Email</p>
        <p>{{ guaranteedTransaction.email }}</p>
      </div>

      <div class="user-info">
        <p>CEP</p>
        <p>{{ guaranteedTransaction.zipCode | zipCodeFilter }}</p>
      </div>
    </div>

    <h4 class="title mt-36">
      RESUMO DO PEDIDO
    </h4>
    <p class="small-regular gray-500">
      {{ guaranteedTransaction.orderId }}
    </p>

    <p class="retailer-name">
      {{ guaranteedTransaction.retailerName }}
    </p>
    <div class="d-flex mt-4 justify-content-space-between gray-500 small-regular">
      <p>Realizado em {{ guaranteedTransaction.purchasedAt | dateFilter }}</p>
      <p>{{ guaranteedTransaction.products.length }} item</p>
    </div>

    <div
      v-for="(product, key) in guaranteedTransaction.products"
      :key="key"
      class="products--wrapper"
    >
      <img :src="product.image" class="product-image" alt="Imagem do produto">
      <div class="w-100">
        <p class="body-semi-bold gray-900">{{ product.name }}</p>
        <div class="d-flex justify-content-space-between">
          <p class="small-regular gray-500">Qtd: {{ product.amount }}</p>
          <p class="small-regular gray-900">{{ product.value | moneyFilter }}</p>
        </div>
      </div>
    </div>

    <div class="price-conditions">
      <div class="row">
        <p class="body-regular">Parcelas</p>
        <div class="text-align-right">
          <p class="body-semi-bold">
            {{ guaranteedTransaction.numberOfInstallments }} vezes de
            {{ guaranteedTransaction.paymentInstallments | moneyFilter }}
          </p>
          <small class="x-small-regular">
            c/juros de {{ guaranteedTransaction.cetPM | interestRateFilter }} a.m.
          </small>
        </div>
      </div>

      <div class="row">
        <p class="body-regular">Valor total</p>
        <p class="body-semi-bold">
          {{ guaranteedTransaction.totalAmountWithoutDownPayment | moneyFilter }}
        </p>
      </div>
    </div>

    <div class="square-checkbox--wrapper">
      <SquareCheckbox
        @input="handleCCBAgree"
        :checked="ccbAgree"
      >
        <p class="body-regular gray-900">
          Estou de acordo com a
          <a
            class="text-decoration-none"
            :href="CCBUrl"
            target="_blank"
          >
            Cédula de Crédito Bancário
          </a>
        </p>
      </SquareCheckbox>

      <SquareCheckbox
        @input="handlePolicyAgree"
        :checked="policyAgree"
      >
        <p class="body-regular gray-900">
          Estou de acordo com os
          <a
            class="text-decoration-none"
            href="https://www.parcelex.com.br/uso"
            target="_blank"
          >
            Termos de uso
          </a> e
          <a
            class="text-decoration-none"
            href="https://www.parcelex.com.br/privacidade"
            target="_blank"
          >
            Política de privacidade
          </a>
          do Parcelex
        </p>
      </SquareCheckbox>
    </div>

    <div class="d-flex justify-content-center">
      <button
        type="button"
        class="button primary rounded body-semi-bold w-100 mt-24"
        @click="handleGuarantorAgreement"
      >
        Aceito ser avalista
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { QuestionMarkCircleIcon } from '@vue-hero-icons/solid';
import SquareCheckbox from '@/components/common/SquareCheckbox.vue';
import StringMixin from '@/mixins/string';

export default {
  name: 'guarantor-agreement',
  mixins: [StringMixin],
  data: () => ({
    ccbAgree: false,
    policyAgree: false,
    tooltipActive: false,
  }),
  computed: {
    ...mapState('guarantor', [
      'guaranteedTransaction',
      'CCBUrl',
      'nextPage',
    ]),
  },
  methods: {
    ...mapActions('guarantor', [
      'signGuarantorApplication',
      'setLoading',
    ]),
    handleCCBAgree(newValue) {
      this.ccbAgree = newValue;
    },
    handlePolicyAgree(newValue) {
      this.policyAgree = newValue;
    },
    handleTooltipClick() {
      this.tooltipActive = !this.tooltipActive;
    },
    async handleGuarantorAgreement() {
      if (this.ccbAgree && this.policyAgree) {
        this.setLoading({ isLoading: true, loadingMessage: 'Aceitando...' });

        await this.signGuarantorApplication();

        this.$router.replace({
          name: this.nextPage,
          query: this.$route.query,
        });

        this.setLoading({ isLoading: false });
      }
    },
  },
  components: {
    SquareCheckbox,
    QuestionMarkCircleIcon,
  },
};
</script>

<style lang="scss" scoped>
  @import '../styles/index.scss';

  .guarantor-agreement {
    margin-top: 36px;

    @media (min-width: $breakpoint-large) {
      margin-top: 26px;
    }
  }

  .product-image {
    width: 48px;
    height: 48px;
  }

  .with-tooltip {
    display: flex;
    align-items: flex-end;
    gap: 6px;
  }

  .tooltip {
    position: absolute;
    max-width: 256px;
    background-color: #0F172A;

    padding: 13px 12px;

    border-radius: 8px;

    p {
      color: #FFFFFF;
    }
  }

  .user-info--wrapper {
    @media (min-width: $breakpoint-large) {
      display: grid;
      grid-template-columns: auto auto;
    }
  }

  .user-info {
    &:first-child {
      margin-top: 16px;
    }

    &:not(:first-child) {
      margin-top: 20px;
    }

    p:first-child {
      @extend .body-regular;
      @extend .gray-500;
    }

    p:last-child {
      @extend .body-regular;
      @extend .gray-900;
    }
  }

  .retailer-name {
    @extend .body-regular;
    @extend .gray-900;
    margin-top: 16px;

    @media (min-width: $breakpoint-large) {
      margin-top: 20px;
      font-weight: 600;
    }
  }

  .products--wrapper {
    display: flex;
    gap: 12px;
    margin-top: 20px;

    @media (min-width: $breakpoint-large) {
      margin-top: 24px;
    }
  }

  .price-conditions {
    @extend .gray-900;

    background-color: #F1F5F9;

    padding: 12px;
    margin-top: 36px;

    .row {
      display: flex;
      justify-content: space-between;

      &:not(:first-child) {
        margin-top: 12px;
      }
    }

    @media (min-width: $breakpoint-large) {
      margin-top: 32px;

      .row {
        &:not(:first-child) {
          margin-top: 16px;
        }
      }
    }
  }

  .square-checkbox--wrapper {
    margin-top: 16px;

    p {
      margin-top: 16px;
    }

    @media (min-width: $breakpoint-large) {
      margin-top: 4px;
    }
  }

  .button {
    -webkit-tap-highlight-color: transparent;
    width: 100%;
    font-weight: 600 !important;

    &.primary {
      background-color: var(--primary);
      color: var(--white);
    }

    &.rounded {
      border-radius: 26px;
      height: 52px;
    }

    @media (min-width: $breakpoint-large) {
      max-width: 184px;
    }
  }
</style>
